// src/pages/OrderStatusPage.tsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import TickIcon from "../assets/svg/tick.svg";
import FailIcon from "../assets/svg/fail.svg";
import { OrderResponse } from "../models/orderStatus_model";
import { useCart } from "../context/cart_context";

const API_BASE = "https://manthrisnonprod.azurewebsites.net";

export const OrderStatusPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { clearCart } = useCart();

  const queryParams = new URLSearchParams(location.search);
  const idFromQuery = queryParams.get("id");
  const orderId = (params?.id ?? idFromQuery) ?? undefined;

  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState<OrderResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    clearCart();
    if (!orderId) {
      setError("Missing order id in URL.");
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/users/orders/${orderId}/payment-status`);
        if (!res.ok) { 
            setError("Missing order id in URL.");
            return;
        }
        const json = await res.json();
        setOrderData(json);
      } catch (err: any) {
        setError(err?.message || "Failed to fetch order");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const order = orderData && orderData.length > 0 ? orderData[0] : null;

  const formatCurrency = (val?: number) =>
    val !== undefined && val !== null ? `₹${(val / 100)}` : "₹0.00";

  // Inline computed responsive style adjustments
  const responsive = {
    outerCard: {
      padding: isMobile ? 18 : 30,
      borderRadius: isMobile ? 12 : 16,
      maxWidth: isMobile ? "100%" : 1100,
      minWidth: isMobile ? undefined : "60%"
    } as React.CSSProperties,
    tick: {
      width: isMobile ? 72 : 100,
      height: isMobile ? 72 : 100,
    } as React.CSSProperties,
    headerText: {
      fontSize: isMobile ? 18 : 24,
    } as React.CSSProperties,
    rowContainer: {
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "stretch" : "flex-start",
      gap: isMobile ? 14 : 20,
    } as React.CSSProperties,
    whiteCard: {
      width: "100%",
      maxWidth: isMobile ? "100%" : 880,
      paddingLeft: isMobile ? 14: 20,
       paddingRight:isMobile ? 14 : 20,
       paddingBottom: isMobile? 14: 15,
      boxSizing: "border-box" as const,
    },
    rightCol: {
      width: "100%",
      maxWidth: isMobile ? "100%" : 480,
    } as React.CSSProperties,
    primaryBtn: {
      width: isMobile ? "100%" : undefined,
    } as React.CSSProperties,
    pagePadding: {
      padding: isMobile ? "16px" : "40px 20px",
    } as React.CSSProperties,
  };

  // Loading state
  if (loading)
    return (
      <div style={{ ...styles.page, ...responsive.pagePadding }}>
        <style>{keyframes}</style>
        <div style={styles.centerBox}>
          <div style={styles.spinner}></div>
          <p style={{ marginTop: 20 }}>Processing order confirmation...</p>
        </div>
      </div>
    );

  // Error / missing order
  if (error || !order)
    return (
      <div style={{ ...styles.page, ...responsive.pagePadding }}>
        <style>{keyframes}</style>
        <div style={{ ...styles.outerCard, ...responsive.outerCard }}>
          <div style={{ ...styles.header, textAlign: "center" }}>
            <img src={FailIcon} alt="failed" style={{ ...styles.tick, ...responsive.tick }} />
            <h2 style={{ ...styles.failTitle, textAlign: "center" }}>Transaction Failed</h2>
            <p style={{ ...styles.failSubtitle, textAlign: "center" }}>
              Your order has failed. If any money was deducted, it will be refunded.
            </p>
            <button
              style={{ ...styles.primaryBtn, ...responsive.primaryBtn, marginTop: 18 }}
              onClick={() => navigate("/")}
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );

  // Normal success state
  const items = order?.order_details || [];
  const address = order?.order_addresses?.[0];
  const payment = order?.order_payment_details?.[0];

  return (
    <div style={{ ...styles.page, ...responsive.pagePadding }}>
      <style>{keyframes}</style>

      <div style={{ ...styles.outerCard, ...responsive.outerCard }}>
        <div style={{ ...styles.header, textAlign: "center" }}>
          <img src={TickIcon} alt="success" style={{ ...styles.tick, ...responsive.tick }} />
          <h2 style={{ ...styles.title, ...responsive.headerText }}>Your order is confirmed</h2>
          <p style={styles.subtitle}>Manthri’s will start working on this right away</p>
          <p style={styles.note}>We’ll email you as soon as it ships.</p>

          <button
            style={{ ...styles.primaryBtn, ...responsive.primaryBtn, marginTop: 18 }}
            onClick={() => navigate("/")}
          >
            Continue Shopping
          </button>
        </div>

        <div style={{ ...styles.rowContainer, ...responsive.rowContainer }}>
          {/* Left column (order details) */}
          <div style={{ ...styles.whiteCard, ...responsive.whiteCard }}>
            <h3 style={styles.sectionTitle}>Order Details</h3>

            {items.length > 0 ? (
  items.map((item, idx) => (
    <React.Fragment key={idx}>
      <div style={styles.itemCard}>
        <img src={item.item_image} alt={item.title} style={styles.itemImage} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Item Title */}
          <p style={styles.itemName}>{item.title}</p>

          {/* Price below title */}
          <div style={{ fontWeight: 600, marginTop: 4 }}>
            {formatCurrency(item.final_price)}
          </div>

          {/* Quantity below price with spacing */}
          <p style={{ ...styles.itemQty, marginTop: 8 }}>
            <span style={{ fontWeight: 500 }}>Qty:</span> <span>{item.quantity}</span>
          </p>
        </div>
      </div>
      {idx < items.length - 1 && <div style={styles.divider} />}
    </React.Fragment>
  ))
) : (
  <p>No items found</p>
)}

          </div>

          {/* Right column */}
          <div style={{ ...styles.rightCol, ...responsive.rightCol }}>
<div style={{ 
    backgroundColor: "#fff", 
    borderRadius: 12, 
    width: "100%", 
    maxWidth: 480, 
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)", 
    display: "flex", 
    flexDirection: "column" 
  }}
>
  {/* Confirmation Number Section with colored top */}
  <div
    style={{
      backgroundColor: "#F6CDC3",
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      padding: 15,
      fontWeight: 600,
    }}
  >
    <h3 style={{ margin: 0 }}>Confirmation Number</h3>
    <div style={styles.confirmationBox}>{order?.order_number ?? "—"}</div>
  </div>

  {/* Payment Details Section */}
  <div style={{ padding: "0px 20px" }}>
    <h3 style={styles.sectionTitle}>Payment Details</h3>
    <div style={styles.paymentRow}>
      <span>Subtotal</span>
      <span>{formatCurrency(order?.total_original_price)}</span>
    </div>
    <div style={styles.paymentRow}>
      <span>Discount</span>
      <span>{formatCurrency(order?.total_discount)}</span>
    </div>
    <div style={styles.paymentRow}>
      <span>Shipping</span>
      <span>{formatCurrency(order?.shipping_charges)}</span>
    </div>
    <div style={{ ...styles.paymentRow, fontWeight: 600 }}>
      <span>Total</span>
      <span>{formatCurrency(order?.total_price)}</span>
    </div>

    <div style={styles.divider} />
    <p>Mode: {payment?.payment_mode ?? "—"}</p>
    <p>Status: {payment?.payment_status ?? "—"}</p>
    <p>ID: {payment?.payment_id ?? "—"}</p>
  </div>
</div>



            <div style={{ ...styles.whiteCard, ...responsive.whiteCard }}>
              <h3 style={styles.sectionTitle}>Shipping Address</h3>
              {address ? (
                <>
                  <p style={styles.addrLine}>
                    {address.first_name} {address.last_name}
                  </p>
                  <p style={styles.addrLine}>
                    {address.address1}, {address.city}
                  </p>
                  <p style={styles.addrLine}>
                    {address.state} - {address.zip_code}
                  </p>
                  <p style={styles.addrLine}>📞 {address.phone_number}</p>
                </>
              ) : (
                <p>No address available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Spinner keyframes
const keyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

const styles: Record<string, React.CSSProperties> = {
  page: {
    backgroundColor: "#ffffff",
    minHeight: "100vh",
    fontFamily: "Arimo, sans-serif",
    display: "flex",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  outerCard: {
    backgroundColor: "#F8F1F1",
    boxShadow: "0 6px 30px rgba(0,0,0,0.06)",
    boxSizing: "border-box",
  },
  header: {
    marginBottom: 24,
  },
  tick: {
    width: 100,
    height: 100,
    display: "block",
    margin: "0 auto",
  },
  title: {
    color: "#2E7D32",
    fontWeight: 700,
    marginTop: 12,
    textAlign: "center",
    fontSize: 24,
  },
  subtitle: {
    color: "#333",
    marginTop: 8,
    textAlign: "center",
  },
  note: {
    color: "#777",
    marginTop: 6,
    fontSize: 14,
    textAlign: "center",
  },
  primaryBtn: {
    marginTop: 16,
    padding: "10px 24px",
    backgroundColor: "#e91e63",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontWeight: 600,
    cursor: "pointer",
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
  },
  rowContainer: {
    display: "flex",
    gap: 20,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  whiteCard: {
    backgroundColor: "#fff",
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  },
  rightCol: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: 12,
  },
  itemCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  itemImage: {
    width: 60,
    height: 90,
    borderRadius: 8,
    objectFit: "cover",
    backgroundColor: "#fafafa",
    flexShrink: 0,
  },
  itemName: {
    fontWeight: 600,
    fontSize: 15,
    margin: 0
  },
  itemQty: {
    color: "#666",
    fontSize: 14,
    margin: 0,
  },
  divider: {
    height: 1,
    backgroundColor: "#eee",
    margin: "10px 0",
  },
  confirmationBox: {
    backgroundColor: "#F6CDC3",
    borderRadius: 8,
    fontWeight: 500,
    marginTop: 5
  },
  addrLine: {
    color: "#555",
    fontSize: 14,
  },
  centerBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "80vh",
    padding: 20,
    boxSizing: "border-box",
  },
  spinner: {
    width: 50,
    height: 50,
    border: "6px solid #f3f3f3",
    borderTop: "6px solid #e91e63",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  paymentRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    fontSize: 15,
    color: "#333",
  },
  failTitle: {
    color: "#D32F2F",
    fontWeight: 700,
    marginTop: 12,
  },
  failSubtitle: {
    color: "#444",
    marginTop: 10,
    fontSize: 15,
  },
};

export default OrderStatusPage;
